import { Component, computed, effect, inject, Signal } from '@angular/core';
import { ComponentsModule, DialogService, SnackBarService, TabItem } from '@mastechnicaegroup/carelinelive-ui';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { PaginationCardComponent } from '@shared/pagination/pagination-card.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SettingsService } from '@lib/setting/settings.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, map, of, shareReplay, startWith, switchMap, take, tap } from 'rxjs';
import { Result, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { PaginatedApiResponse } from '@lib/api/responses/paginated-api-response';
import { HandbackReasons } from '@lib/api/models/setting';
import { AddHandbackReasonDialogComponent } from '../../dialogs/add/add-handback-reason-dialog.component';
import { EditHandbackReasonDialogComponent } from '../../dialogs/edit/edit-handback-reason-dialog.component';
import { PromptHandbackStatusChangeDialogComponent } from '../../dialogs/prompt/prompt-handback-status-change-dialog.component';
import { AsyncPipe } from '@angular/common';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { postParamNamesFilterActive, postParamNamesFilterQuery } from '@shared/filters/filter-params';

@Component({
    selector: 'app-setting-handback-reasons-index-page',
    imports: [
        ComponentsModule,
        ReactiveFormsModule,
        FormsModule,
        HeaderComponent,
        PaginationCardComponent,
        AsyncPipe,
        ErrorPanelComponent
    ],
    templateUrl: './setting-handback-reasons-index-page.component.html',
    standalone: true,
    styleUrl: './setting-handback-reasons-index-page.component.scss'
})
export class SettingHandbackReasonsIndexPageComponent {

    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _authService = inject(AuthenticationService);
    private readonly _dialog = inject(DialogService);
    private readonly _settingService = inject(SettingsService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _router = inject(Router);
    private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);

    handbackReasons?: Record<string, string | number>[];

    protected user = toSignal(this._authService.user$);

    /** The search form control */
    protected readonly searchControl = new FormControl<string>(
        this._activatedRoute.snapshot.queryParams['q'] ?? '',
        [Validators.maxLength(50)]
    );

    /** An observable that emits the search query */
    protected readonly searchQuery = toSignal(this.searchControl.valueChanges.pipe(
        startWith(this.searchControl.value),

        // Only fire once every 100ms
        debounceTime(100),

        // If it's an empty string, return null
        map(value => value ? value : undefined)
    ));

    protected readonly tabItems: TabItem[] = [
        {
            disabled: false,
            id: '0',
            title: 'All'
        },
        {
            disabled: false,
            id: '1',
            title: 'Active'
        },
        {
            disabled: false,
            id: '2',
            title: 'Inactive'
        }
    ];

    protected currentTabId$ = this._activatedRoute.queryParams.pipe(
        map((params): string => params['tab'] ?? '0'),
        distinctUntilChanged(),
        shareReplay(1)
    );

    /** The current page being shown */
    protected readonly page = toSignal<number>(this._activatedRoute.queryParams.pipe(
        map((params): number => {
            const pageParam = Number(params['page']);
            return !Number.isNaN(pageParam) && pageParam > 0 ? pageParam : 1;
        })
    ));

    protected readonly pageResult: Signal<Result<PaginatedApiResponse<HandbackReasons>> | undefined> = toSignal(combineLatest({
        page: toObservable(this.page),
        tabId: this.currentTabId$,
        refresh: this.refreshTrigger$.asObservable(),
        searchQuery: toObservable(this.searchQuery)
    }).pipe(
        switchMap(({ page, searchQuery, tabId }) => this._settingService.listHandbackReasons(page, {
            [postParamNamesFilterQuery]: searchQuery,
            [postParamNamesFilterActive]: tabId === '0' ? '' : tabId === '1' ? 'true' : 'false'
        }).pipe(
            // Turn into a Result
            toResult()
        )),

        // Share the same result with all new subscribers
        shareReplay(1)
    ));

    /** The pagination meta data */
    protected readonly meta = computed(() => {
        // Extract the meta-data from the page result
        return this.pageResult()?.value?.meta;
    });

    constructor() {
        // Update the query params when the page or search query changes
        effect(() => {
            this._router.navigate([], {
                relativeTo: this._activatedRoute,
                queryParams: {
                    q: this.searchQuery()
                },
                queryParamsHandling: 'merge',

                // Don't push a new history state, just replace the query param
                replaceUrl: true
            });
        });
    }

    protected nextPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page + 1);
        }
    }

    protected previousPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page - 1);
        }
    }

    protected async setPage(page: number) {
        await this._router.navigate([], {
            queryParams: { page },
            queryParamsHandling: 'merge'
        });
    }

    addHandbackReason() {
        return AddHandbackReasonDialogComponent.show(this._dialog).closed.pipe(take(1)).subscribe(() => {
            this.refreshTrigger$.next();
        });
    }

    changeStatus(handbackReason: HandbackReasons, status: boolean) {

        const handbackReasonData = {
            ...handbackReason,
            status: status
        };

        return PromptHandbackStatusChangeDialogComponent.show(this._dialog, handbackReasonData).closed.pipe(
            tap(() => {
                this.refreshTrigger$.next();
            }),
            catchError(() => {

                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Handback Reason failed to change status',
                    actions: [
                        { id: 'undo', label: 'OK' }
                    ]
                });

                return of(null);
            })
        ).subscribe();
    }

    editHandbackReason(handbackReason: HandbackReasons) {
        return EditHandbackReasonDialogComponent.show(this._dialog, handbackReason).closed.pipe(
            tap(() => {
                this.refreshTrigger$.next();
            }),
            catchError(() => {

                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Handback Reason failed to edit',
                    actions: [
                        { id: 'undo', label: 'OK' }
                    ]
                });

                return of(null);
            })
        ).subscribe();
    }

    async currentTabIdChange($event: string) {
        await this._router.navigate([], {
            queryParams: { tab: $event },
            queryParamsHandling: 'merge'
        });
    }
}
